import {
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  collection,
  onSnapshot,
  writeBatch,
  getDocs,
  WriteBatch,
  getDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { getDownloadURL, uploadBytes } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

import { MyRef, myStorage, firestore } from 'Common/Firebase';
import { ICL, Task } from 'shared/types';

import { SummarizeTask } from '../shared/types/task';

const colName = 'Works';
const subColName = 'Tasks';

const getDocRef = (ICLId: ICL['id'], taskId: Task['id']) => {
  if (typeof ICLId !== 'string' || typeof taskId !== 'string') {
    throw new Error('ICLId and taskId must be strings');
  }

  return doc(firestore, colName, ICLId, subColName, taskId);
};

const getSubColRef = (ICLId: ICL['id']) => collection(firestore, colName, ICLId, subColName);

interface IdType {
  id: string;
}

async function addTask<SpecTaskType extends IdType>(ICLId: ICL['id'], task: SpecTaskType) {
  const newTask = task;
  const docRef = getDocRef(ICLId, task.id);

  await setDoc(docRef, newTask);

  return task.id;
}

async function updateTaskOrder(ICLId: string, orderedTasks: { id: string }[]) {
  const snapshot = await getDocs(getSubColRef(ICLId));
  const savedIds = new Set(snapshot.docs.map(task => task.id));
  const batch = writeBatch(firestore);

  orderedTasks.forEach((task, indexNumber) => {
    // New tasks stay local until their own form is saved.
    if (savedIds.has(task.id)) {
      batch.update(getDocRef(ICLId, task.id), { indexNumber });
    }
  });

  await batch.commit();
}

async function updateTask<SpecTaskType extends IdType>(ICLId: ICL['id'], task: SpecTaskType) {
  const docRef = getDocRef(ICLId, task.id);

  // Content/answer saves must not overwrite a position captured before a reorder.
  const { indexNumber, ...content } = task as SpecTaskType & { indexNumber?: number };
  await updateDoc(docRef, content);
}

async function removeTask<SpecTaskType extends IdType>(ICLId: ICL['id'], taskId: SpecTaskType['id']) {
  const docRef = getDocRef(ICLId, taskId);

  await deleteDoc(docRef);
}

interface SubscribeToICLTasksParams {
  setNewTasks: (tasks: SummarizeTask[]) => void;
  ICLId: ICL['id'];
}

function subscribeToICLTasks({ ICLId, setNewTasks }: SubscribeToICLTasksParams) {
  const fetchWithOrder = () => {
    const subColRef = query(getSubColRef(ICLId), orderBy('indexNumber'));

    return onSnapshot(subColRef, snapshot => {
      const ICLs: SummarizeTask[] = [];

      snapshot.forEach(doc => ICLs.push(doc.data() as SummarizeTask));

      setNewTasks(ICLs);
    });
  };

  return fetchWithOrder();
}

async function getICLTasks(ICLId: ICL['id']): Promise<Task[]> {
  const fetchWithOrder = async () => {
    try {
      const subColRef = query(getSubColRef(ICLId), orderBy('indexNumber'));
      const docsSnapshot = await getDocs(subColRef);
      const tasks: Task[] = [];

      docsSnapshot.forEach(doc => {
        tasks.push(doc.data() as Task);
      });

      return tasks;
    } catch (error) {
      console.error('Error fetching with order:', error);
      return [];
    }
  };

  const fetchWithoutOrder = async () => {
    try {
      const subColRef = getSubColRef(ICLId);
      const docsSnapshot = await getDocs(subColRef);
      const tasks: Task[] = [];

      docsSnapshot.forEach(doc => {
        tasks.push(doc.data() as Task);
      });

      return tasks;
    } catch (error) {
      console.error('Error fetching without order:', error);
      return [];
    }
  };

  const tasks = await fetchWithOrder();

  if (tasks.length === 0) {
    return await fetchWithoutOrder();
  }

  return tasks;
}

async function removeICLTasks(ICLId: ICL['id'], tasks: SummarizeTask[]) {
  if (tasks.length) {
    const batch = writeBatch(firestore);

    tasks.forEach(task => {
      const docRef = getDocRef(ICLId, task.id);
      batch.delete(docRef);
    });

    await batch.commit();
  }
}

async function copyICLTasks(prevICLId: ICL['id'], newICLId: ICL['id']) {
  const tasks = await getICLTasks(prevICLId);

  if (tasks.length) {
    const batch = writeBatch(firestore);
    const tasksCopy = tasks.map(async task => {
      const newId = uuidv4();
      const newTask = { ...task, id: newId }; // overwrite ICL ID

      if (task.taskPhotos && task.taskPhotos.length > 0) {
        newTask.taskPhotos = await prepareTaskPhotosForCopying(task.taskPhotos);
      }

      if (task.videoUrl) {
        newTask.videoUrl = await prepareTasksVideoForCopying(task.videoUrl);
      }

      if (task.audioUrl) {
        newTask.audioUrl = await prepareTasksAudioForCopying(task.audioUrl);
      }

      const docRef = getDocRef(newICLId.id ?? newICLId, newId);
      batch.set(docRef, newTask);
    });

    await Promise.all(tasksCopy);
    await batch.commit();
  }
}

async function prepareTaskPhotosForCopying(taskPhotos: string[]) {
  const newTaskPhotos = [];

  for (const photoUrl of taskPhotos) {
    const response = await fetch(photoUrl);
    const blob = await response.blob();

    const startingIndex = photoUrl.lastIndexOf('/') + 10 + 40; // 10 = part of the url '/Images%2F', 40 = id of the file
    const endingIndex = photoUrl.lastIndexOf('?');
    const nameOfTheFile = photoUrl.slice(startingIndex, endingIndex);

    const newId = uuidv4();
    const newFileName = `id-${newId}-${nameOfTheFile}`; // each file name in storage must have a unique name otherwise it is not possible to add two different files with the same name

    const storageRef = MyRef(myStorage, 'Images/' + newFileName);
    const snapshot = await uploadBytes(storageRef, blob);

    const newPhotoUrl = await getDownloadURL(snapshot.ref);

    newTaskPhotos.push(newPhotoUrl);
  }

  return newTaskPhotos;
}

async function prepareTasksVideoForCopying(taskVideo: string) {
  const response = await fetch(taskVideo);
  const blob = await response.blob();

  const startingIndex = taskVideo.lastIndexOf('/') + 9 + 40; // 9 = part of the url '/Video%2F', 40 = id of the file
  const endingIndex = taskVideo.lastIndexOf('?');
  const nameOfTheFile = taskVideo.slice(startingIndex, endingIndex);

  const newId = uuidv4();
  const newFileName = `id-${newId}-${nameOfTheFile}`; // each file name in storage must have a unique name otherwise it is not possible to add two different files with the same name

  const storageRef = MyRef(myStorage, 'Video/' + newFileName);
  const snapshot = await uploadBytes(storageRef, blob);

  const newVideoUrl = await getDownloadURL(snapshot.ref);

  return newVideoUrl;
}

async function prepareTasksAudioForCopying(taskAudio: string) {
  const response = await fetch(taskAudio);
  const blob = await response.blob();

  const startingIndex = taskAudio.lastIndexOf('/') + 9 + 40; // 9 = part of the url '/Audio%2F', 40 = id of the file
  const endingIndex = taskAudio.lastIndexOf('?');
  const nameOfTheFile = taskAudio.slice(startingIndex, endingIndex);

  const newId = uuidv4();
  const newFileName = `id-${newId}-${nameOfTheFile}`; // each file name in storage must have a unique name otherwise it is not possible to add two different files with the same name

  const storageRef = MyRef(myStorage, 'Audio/' + newFileName);
  const snapshot = await uploadBytes(storageRef, blob);

  const newVideoUrl = await getDownloadURL(snapshot.ref);

  return newVideoUrl;
}

async function prepareTasksWordForCopying(taskWord: string) {
  const response = await fetch(taskWord);
  const blob = await response.blob();

  const startingIndex = taskWord.lastIndexOf('/') + 8 + 40; // 8 = part of the url '/Word%2F', 40 = id of the file
  const endingIndex = taskWord.lastIndexOf('?');
  const nameOfTheFile = taskWord.slice(startingIndex, endingIndex);

  const newId = uuidv4();
  const newFileName = `id-${newId}-${nameOfTheFile}`; // each file name in storage must have a unique name otherwise it is not possible to add two different files with the same name

  const storageRef = MyRef(myStorage, 'Word/' + newFileName);
  const snapshot = await uploadBytes(storageRef, blob);

  const newWordUrl = await getDownloadURL(snapshot.ref);

  return newWordUrl;
}

async function prepareTasksPdfForCopying(taskPdf: string) {
  const response = await fetch(taskPdf);
  const blob = await response.blob();

  const startingIndex = taskPdf.lastIndexOf('/') + 7 + 40; // 7 = part of the url '/Pdf%2F', 40 = id of the file
  const endingIndex = taskPdf.lastIndexOf('?');
  const nameOfTheFile = taskPdf.slice(startingIndex, endingIndex);

  const newId = uuidv4();
  const newFileName = `id-${newId}-${nameOfTheFile}`; // each file name in storage must have a unique name otherwise it is not possible to add two different files with the same name

  const storageRef = MyRef(myStorage, 'Pdf/' + newFileName);
  const snapshot = await uploadBytes(storageRef, blob);

  const newPdfUrl = await getDownloadURL(snapshot.ref);

  return newPdfUrl;
}

async function prepareTasksForCopying(prevICLId: ICL['id'], batch: WriteBatch, newICLIds: string[]) {
  const tasks = await getICLTasks(prevICLId);

  if (tasks.length) {
    for (const newICLId of newICLIds) {
      const tasksCopy = tasks.map(async task => {
        const newId = uuidv4();
        const newTask: Task = {
          ...task,
          answer: '',
          answersPhotos: [],
          editedAnswerPhotos: [],
          checkedAt: '',
          completedAt: '',
          id: newId,
        };

        if (task.taskPhotos && task.taskPhotos.length > 0) {
          newTask.taskPhotos = await prepareTaskPhotosForCopying(task.taskPhotos);
        }

        if (task.videoUrl) {
          newTask.videoUrl = await prepareTasksVideoForCopying(task.videoUrl);
        }

        if (task.audioUrl) {
          newTask.audioUrl = await prepareTasksAudioForCopying(task.audioUrl);
        }

        if (task.wordUrl) {
          newTask.wordUrl = await prepareTasksWordForCopying(task.wordUrl);
        }

        if (task.pdfUrl) {
          newTask.pdfUrl = await prepareTasksPdfForCopying(task.pdfUrl);
        }

        const docRef = getDocRef(newICLId, newId);
        batch.set(docRef, newTask);
      });

      await Promise.all(tasksCopy);
    }
  }
}

async function getTask(ICLId: ICL['id'], taskId: Task['id']) {
  const docRef = getDocRef(ICLId, taskId);
  const snapshot = await getDoc(docRef);

  return snapshot.data();
}

export const taskApi = {
  getTask,
  addTask,
  removeTask,
  updateTaskOrder,
  updateTask,
  getICLTasks,
  subscribeToICLTasks,
  removeICLTasks,
  copyICLTasks,
  prepareTasksForCopying,
};
